import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ChartComponent from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import Img from '../../../../Img';
import {
    buildHistogramData,
    buildLandscapeHistogramOptions,
    buildHistogramAnchorPlugin,
} from '../performanceStatsUtils';
import { ROUNDED_GRADIENT_BAR_TYPE, registerRoundedGradientBarType } from '../roundedGradientBar';
import styles from '../performanceStats.scss';

registerRoundedGradientBarType();

const getViewportSize = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
});

const LandscapeHistogramOverlay = ({ singles, currentUtr, rangeLabel, onClose, 'data-testid': dataTestId }) => {
    const [viewport, setViewport] = useState(getViewportSize);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        const handleResize = () => setViewport(getViewportSize());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const histogram = singles?.histogram;
    const histogramData = useMemo(() => buildHistogramData(histogram), [histogram]);
    const histogramOptions = useMemo(() => buildLandscapeHistogramOptions(histogram), [histogram]);
    const chartPlugins = useMemo(
        () => [ChartDataLabels, buildHistogramAnchorPlugin(histogram, currentUtr)],
        [histogram, currentUtr],
    );

    if (typeof document === 'undefined') { return null; }

    const rotatorStyle = {
        width: `${viewport.height}px`,
        height: `${viewport.width}px`,
    };

    return ReactDOM.createPortal(
        <div className={styles.landscapeOverlay} data-testid={dataTestId}>
            <div className={styles.landscapeRotator} style={rotatorStyle}>
                <div className={`${styles.landscapeChartPanel} ${styles.landscapeChartPanelFull}`}>
                    {rangeLabel && (
                        <div className={`${styles.chartHeaderRow} ${styles.landscapeHeaderRow}`} data-testid={dataTestId && `${dataTestId}.range`}>
                            <span className={styles.chartHeaderLabel} data-testid={dataTestId && `${dataTestId}.range.label`}>Performance Range:</span>
                            <span className={styles.chartHeaderValue} data-testid={dataTestId && `${dataTestId}.range.value`}>{rangeLabel}</span>
                        </div>
                    )}
                    <div className={styles.landscapeChartCanvas} data-testid={dataTestId && `${dataTestId}.chart`}>
                        <ChartComponent
                            type={ROUNDED_GRADIENT_BAR_TYPE}
                            data={histogramData}
                            options={histogramOptions}
                            plugins={chartPlugins}
                        />
                    </div>
                    <div className={`${styles.landscapeFooterRow} ${styles.footerRowEnd}`}>
                        <button
                            type="button"
                            className={styles.expandTriggerBtn}
                            onClick={onClose}
                            data-testid={dataTestId && `${dataTestId}.collapse-btn`}
                        >
                            <Img src="/img/icons/rotate-to-portrait-icon.svg" alt="Back to portrait" className={styles.expandTriggerIcon} data-testid={dataTestId && `${dataTestId}.collapse-btn.icon`} />
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

LandscapeHistogramOverlay.propTypes = {
    singles: PropTypes.shape({
        histogram: PropTypes.shape({
            data: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }),
    currentUtr: PropTypes.number,
    rangeLabel: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    'data-testid': PropTypes.string,
};

LandscapeHistogramOverlay.defaultProps = {
    singles: null,
    currentUtr: null,
    rangeLabel: null,
    'data-testid': null,
};

export default LandscapeHistogramOverlay;
